import React, { useState } from 'react';
import { Container, Card, Form, Button, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import authService from '../services/authService';

const ChangePassword = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user types
    setError('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    if (!formData.oldPassword) {
      setError('Old password is required');
      return false;
    }

    if (!formData.newPassword) {
      setError('New password is required');
      return false;
    }

    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return false;
    }

    if (!formData.confirmPassword) {
      setError('Please confirm your new password');
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return false;
    }

    if (formData.oldPassword === formData.newPassword) {
      setError('New password must be different from old password');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const response = await authService.changePassword(
        formData.oldPassword,
        formData.newPassword
      );

      if (response.message) {
        setSuccess(response.message);

        // Clear form
        setFormData({
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        });

        // Redirect back to dashboard after 2 seconds
        setTimeout(() => {
          navigate(`/dashboard/${user?.role}`);
        }, 2000);
      }
    } catch (err) {
      setError(err.error || err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/dashboard/${user?.role}`);
  };

  const getPasswordStrength = (password) => {
    if (!password) return { label: '', color: '' };

    const length = password.length;
    const hasNumbers = /\d/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    let strength = 0;
    if (length >= 6) strength++;
    if (length >= 8) strength++;
    if (hasNumbers) strength++;
    if (hasLowerCase && hasUpperCase) strength++;
    if (hasSpecialChar) strength++;

    if (strength <= 2) return { label: 'Weak', color: 'danger' };
    if (strength <= 3) return { label: 'Medium', color: 'warning' };
    return { label: 'Strong', color: 'success' };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  return (
    <>
      <Navbar />
      <Container className="mt-5" style={{ paddingTop: '60px', maxWidth: '600px' }}>
        <Card className="shadow">
          <Card.Header className="bg-primary text-white">
            <h4 className="mb-0">
              <i className="fas fa-key me-2"></i>
              Change Password
            </h4>
          </Card.Header>
          <Card.Body>
            {/* Success Alert */}
            {success && (
              <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                <i className="fas fa-check-circle me-2"></i>
                {success}
              </Alert>
            )}

            {/* Error Alert */}
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                <i className="fas fa-exclamation-circle me-2"></i>
                {error}
              </Alert>
            )}

            {/* Change Password Form */}
            <Form onSubmit={handleSubmit}>
              {/* Old Password */}
              <Form.Group className="mb-3">
                <Form.Label>
                  Old Password <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPasswords.old ? 'text' : 'password'}
                    name="oldPassword"
                    placeholder="Enter your current password"
                    value={formData.oldPassword}
                    onChange={handleInputChange}
                    disabled={loading}
                    required
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => togglePasswordVisibility('old')}
                    disabled={loading}
                  >
                    <i className={`fas fa-eye${showPasswords.old ? '-slash' : ''}`}></i>
                  </Button>
                </InputGroup>
              </Form.Group>

              {/* New Password */}
              <Form.Group className="mb-3">
                <Form.Label>
                  New Password <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPasswords.new ? 'text' : 'password'}
                    name="newPassword"
                    placeholder="Enter new password (min 6 characters)"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    disabled={loading}
                    required
                    minLength={6}
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => togglePasswordVisibility('new')}
                    disabled={loading}
                  >
                    <i className={`fas fa-eye${showPasswords.new ? '-slash' : ''}`}></i>
                  </Button>
                </InputGroup>
                {formData.newPassword && (
                  <div className="mt-2">
                    <small className="text-muted">Password Strength: </small>
                    <span className={`badge bg-${passwordStrength.color}`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                )}
              </Form.Group>

              {/* Confirm New Password */}
              <Form.Group className="mb-4">
                <Form.Label>
                  Confirm New Password <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPasswords.confirm ? 'text' : 'password'}
                    name="confirmPassword"
                    placeholder="Re-enter new password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    disabled={loading}
                    required
                  />
                  <Button
                    variant="outline-secondary"
                    onClick={() => togglePasswordVisibility('confirm')}
                    disabled={loading}
                  >
                    <i className={`fas fa-eye${showPasswords.confirm ? '-slash' : ''}`}></i>
                  </Button>
                </InputGroup>
                {formData.newPassword && formData.confirmPassword && (
                  <div className="mt-2">
                    {formData.newPassword === formData.confirmPassword ? (
                      <small className="text-success">
                        <i className="fas fa-check me-1"></i>
                        Passwords match
                      </small>
                    ) : (
                      <small className="text-danger">
                        <i className="fas fa-times me-1"></i>
                        Passwords do not match
                      </small>
                    )}
                  </div>
                )}
              </Form.Group>

              {/* Password Requirements */}
              <div className="mb-4 p-3 border border-info rounded bg-light">
                <small className="text-muted">
                  <strong>Password Requirements:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Minimum 6 characters long</li>
                    <li>Different from your old password</li>
                    <li>Recommended: Include uppercase, lowercase, numbers, and special characters</li>
                  </ul>
                </small>
              </div>

              {/* Action Buttons */}
              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading}
                  className="flex-grow-1"
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Changing Password...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-lock me-2"></i>
                      Change Password
                    </>
                  )}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  <i className="fas fa-times me-2"></i>
                  Cancel
                </Button>
              </div>
            </Form>

            {/* Security Note */}
            <div className="mt-4 p-3 border border-warning rounded bg-light">
              <small className="text-muted">
                <i className="fas fa-shield-alt me-2 text-warning"></i>
                <strong>Security Tip:</strong> Choose a strong password that you haven't used before.
                Never share your password with anyone.
              </small>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default ChangePassword;
